import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box, CssBaseline, GlobalStyles } from '@mui/material';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import WorkflowPage from './pages/WorkflowPage';
import PracticesPage from './pages/PracticesPage';
import PracticePage from './pages/PracticePage';
import { LoginPage } from './pages/LoginPage';
import { SessionWarning } from './components/SessionWarning';
import { SessionManagement } from './components/SessionManagement';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import EditorRoute from './components/EditorRoute';
import { useThemeContext } from './contexts/ThemeContext';
import { AuthProvider } from './auth/AuthProvider';
import { RBACProvider } from './contexts/RBACContext';
import { WorkflowSearchProvider } from './contexts/WorkflowSearchContext';

// Lazy-loaded pages for code splitting (heavy components)
const ChatPage = lazy(() => import('./pages/ChatPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AgentsPage = lazy(() => import('./pages/AgentsPage'));
const FormDisplayPage = lazy(() => import('./pages/FormDisplayPage'));
const WorkflowStudioPage = lazy(() => import('./pages/WorkflowStudioPage'));
const LoaderTestPage = lazy(() => import('./pages/LoaderTestPage'));
const FormBuilderPage = lazy(() => import('./components/FormBuilder/FormBuilderPage').then(m => ({ default: m.FormBuilderPage })));

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress />
  </Box>
);

function App() {
  const { themeMode, toggleTheme } = useThemeContext();
  const aiChatEnabled = process.env.REACT_APP_AI_CHAT_ENABLED === 'true';

  useEffect(() => {
    const env = process.env.REACT_APP_ENVIRONMENT;
    // Don't add environment suffix for production
    document.title = env && env !== 'prod' ? `HK Workflows - ${env.toUpperCase()}` : 'HK Workflows';
  }, []);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <RBACProvider>
          <WorkflowSearchProvider>
          <CssBaseline />
          <GlobalStyles styles={{
            'input, select, textarea, button': {
              fontSize: '16px',
              lineHeight: 1.4,
            },
          }} />
          <Routes>
        <Route path="/auth/callback" element={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <CircularProgress />
          </Box>
        } />
        
        <Route path="/login" element={<LoginPage />} />
        
        <Route path="/*" element={
          <ProtectedRoute>
            <SessionWarning warningThreshold={5} />
            <Layout themeMode={themeMode} onToggleTheme={toggleTheme}>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/workflow" element={<WorkflowPage />} />
                <Route path="/practices" element={<PracticesPage />} />
                <Route path="/chat" element={aiChatEnabled ? <ChatPage /> : <Navigate to="/" replace />} />
                <Route path="/agents" element={aiChatEnabled ? <AgentsPage /> : <Navigate to="/" replace />} />
                <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
                <Route path="/session" element={<AdminRoute><SessionManagement /></AdminRoute>} />
                <Route path="/studio/*" element={<EditorRoute><WorkflowStudioPage /></EditorRoute>} />
                <Route path="/loader-test" element={<LoaderTestPage />} />
                {process.env.REACT_APP_ENVIRONMENT !== 'prod' && (
                  <Route path="/forms" element={<AdminRoute><FormBuilderPage /></AdminRoute>} />
                )}
                <Route path="/form/:formId" element={<FormDisplayPage />} />
                <Route path="/:practice" element={<PracticePage />} />
                <Route path="/:practice/:workflow" element={<WorkflowPage />} />
              </Routes>
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
          </Routes>
          </WorkflowSearchProvider>
        </RBACProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;